import autobind from 'autobind';
import { computed, observable, makeObservable, override } from 'mobx';
import isNil from 'lodash/isNil';
import every from 'lodash/every';

import {
    IncludeServiceGroupData,
    IncludeServiceParams,
    Nullable,
    Optional,
    PayerType,
    PlanCreateParams,
    PlanData,
    PlanStatus,
    PlanType,
    PlanUpdateParams,
} from 'chaika-frontend-types/frontend-cms';

import { Form, InitParams, ValidationType } from '@store/abstract/Form';
import { removeNilKeys } from '@utils/removeNiKeys';
import { Validation } from '@utils/Validation';
import { Money } from '@store/models/Money';

const MAX_AGE = 100;

export class PlanForm extends Form {
    @observable public readonly id: string;
    @observable public readonly status: PlanStatus;
    @observable public readonly planServiceId?: string;
    @observable protected minAge?: Nullable<number>;
    @observable protected maxAge?: Nullable<number>;
    @observable protected type: PlanType;
    @observable protected payerId?: string;
    @observable protected payerType: PayerType;
    @observable protected name: string;
    @observable protected includeServices: IncludeServiceParams[] = [];
    @observable protected excludeServices: string[] = [];
    @observable protected includeGroups: IncludeServiceGroupData[] = [];
    @observable protected excludeGroups: string[] = [];
    @observable protected allIncluded: boolean = false;
    @observable protected share: number;
    @observable protected discount: number;
    @observable protected moneyLimit?: number;
    @observable protected duration: number;
    @observable protected conditions?: string;
    @observable protected contractId?: string;
    @observable protected startDate: string;
    @observable protected endDate: Optional<string>;
    @observable protected creationTime: string;
    @observable protected updateTime?: string;
    @observable protected versionStartDate: string;
    @observable protected authorId: string;
    constructor(
        {
            id,
            type = PlanType.Insurance,
            payerId,
            payerType = PayerType.Individual,
            name,
            includeServices = [],
            excludeServices = [],
            includeGroups = [],
            excludeGroups = [],
            allIncluded = false,
            share = 0,
            discount = 0,
            duration = 0,
            moneyLimit,
            conditions,
            contractId,
            startDate,
            endDate,
            status,
            creationTime,
            versionStartDate,
            authorId,
            planServiceId,
            minAge,
            maxAge,
        }: PlanData = {} as PlanData,
    ) {
        super();
        makeObservable(this);

        this.id = id;
        this.type = type;
        this.payerId = payerId;
        this.payerType = payerType;
        this.name = name;
        this.includeServices = includeServices;
        this.excludeServices = excludeServices;
        this.includeGroups = includeGroups;
        this.excludeGroups = excludeGroups;
        this.allIncluded = allIncluded;
        this.share = share;
        this.discount = discount;
        this.moneyLimit = moneyLimit ? Money.fromKopecks(moneyLimit).rubles : undefined;
        this.duration = duration;
        this.conditions = conditions;
        this.contractId = contractId;
        this.startDate = startDate;
        this.endDate = endDate;
        this.status = status;
        this.creationTime = creationTime;
        this.versionStartDate = versionStartDate;
        this.authorId = authorId;
        this.planServiceId = planServiceId;
        this.minAge = minAge;
        this.maxAge = maxAge;

        this.init();
    }

    @computed
    public get isPlanned(): boolean {
        return this.status === PlanStatus.Planned;
    }

    @override
    public get isFormValid(): boolean {
        let isValid = every(this.fields, validStatus => this.isFieldValid(validStatus.name));

        if (this.type === PlanType.Insurance) {
            isValid = isValid && !!this.payerId;
        }

        return isValid && !this.isAgeRangeInvalid;
    }

    @computed
    public get isAgeRangeInvalid(): boolean {
        return (
            this.type === PlanType.Checkup && !isNil(this.minAge) && !isNil(this.maxAge) && this.minAge > this.maxAge
        );
    }

    @computed
    public get createParams(): PlanCreateParams {
        return {
            id: this.id,
            type: this.type,
            payerId: this.payerId,
            payerType: this.payerType,
            name: this.name,
            contractId: this.contractId,
            includeServices: this.includeServices.map(params => removeNilKeys(params)),
            excludeServices: this.excludeServices,
            includeGroups: this.includeGroups.map(params => removeNilKeys(params)),
            excludeGroups: this.excludeGroups,
            allIncluded: this.allIncluded,
            share: this.share,
            discount: this.discount,
            moneyLimit: this.moneyLimit ? this.formatMoney(this.moneyLimit) : undefined,
            minAge: this.type === PlanType.Checkup ? (this.minAge ?? undefined) : undefined,
            maxAge: this.type === PlanType.Checkup ? (this.maxAge ?? undefined) : undefined,
            duration: this.duration,
            conditions: this.conditions,
            startDate: this.startDate,
            // @ts-ignore
            endDate: this.endDate,
        };
    }

    @computed
    public get updateParams(): PlanUpdateParams {
        return {
            name: this.name,
            type: this.type,
            payerId: this.payerId,
            contractId: this.contractId,
            payerType: this.payerType,
            includeServices: this.includeServices.map(params => removeNilKeys(params)),
            excludeServices: this.excludeServices,
            includeGroups: this.includeGroups.map(params => removeNilKeys(params)),
            excludeGroups: this.excludeGroups,
            allIncluded: this.allIncluded,
            share: this.share,
            discount: this.discount,
            moneyLimit: this.moneyLimit ? this.formatMoney(this.moneyLimit) : null,
            minAge: this.type === PlanType.Checkup ? (this.minAge ?? null) : null,
            maxAge: this.type === PlanType.Checkup ? (this.maxAge ?? null) : null,
            duration: this.duration,
            conditions: this.conditions,
            startDate: this.startDate,
            // @ts-ignore
            endDate: this.endDate,
            versionStartDate: this.versionStartDate,
        };
    }

    public serialize(): PlanData {
        return {
            id: this.id,
            type: this.type,
            payerId: this.payerId,
            payerType: this.payerType,
            name: this.name,
            includeServices: this.includeServices as any[],
            excludeServices: this.excludeServices,
            includeGroups: this.includeGroups as any[],
            excludeGroups: this.excludeGroups,
            allIncluded: this.allIncluded,
            share: this.share,
            discount: this.discount,
            moneyLimit: this.moneyLimit,
            duration: this.duration,
            conditions: this.conditions,
            contractId: this.contractId,
            startDate: this.startDate,
            endDate: this.endDate,
            status: this.status,
            creationTime: this.creationTime,
            versionStartDate: this.versionStartDate,
            authorId: this.authorId,
            planServiceId: this.planServiceId,
            minAge: this.minAge ?? undefined,
            maxAge: this.maxAge ?? undefined,
        };
    }

    protected getInitParams(): InitParams[] {
        return [
            {
                name: 'type',
                isRequired: true,
                validation: ValidationType.notEmpty,
            },
            {
                name: 'payerId',
            },
            {
                name: 'payerType',
            },
            {
                name: 'name',
                isRequired: true,
                validation: ValidationType.notEmpty,
            },
            {
                name: 'includeServices',
                format: this.formatIncludeServiceParams,
                onUpdateHandler: this.onIncludeServicesSet,
            },
            {
                name: 'excludeServices',
                onUpdateHandler: this.onExcludeServicesSet,
            },
            {
                name: 'includeGroups',
                format: this.formatIncludeServiceParams,
                onUpdateHandler: this.onIncludeGroupsSet,
            },
            {
                name: 'excludeGroups',
                onUpdateHandler: this.onExcludeGroupsSet,
            },
            // DEV-924 добавить allIncluded
            {
                name: 'share',
                isRequired: true,
                format: this.toNumber({ min: 0, max: 100 }),
                validation: ValidationType.NumberString,
            },
            {
                name: 'discount',
                isRequired: true,
                format: this.toNumber({ min: 0, max: 100 }),
                validation: ValidationType.NumberString,
            },
            {
                name: 'moneyLimit',
                isRequired: false,
                format: this.toNumber({ min: 0 }),
                validation: ValidationType.NumberString,
            },
            {
                name: 'minAge',
                format: this.formatAge,
            },
            {
                name: 'maxAge',
                format: this.formatAge,
            },
            {
                name: 'duration',
                format: this.toNumber(),
                validation: ValidationType.NumberString,
            },
            {
                name: 'conditions',
            },
            {
                name: 'contractId',
                isRequired: true,
                validation: [ValidationType.notEmpty],
            },
            {
                name: 'startDate',
                isRequired: true,
                validation: [ValidationType.notEmpty, ValidationType.Date],
            },
            {
                name: 'versionStartDate',
                isRequired: true,
                validation: [ValidationType.notEmpty, ValidationType.Date],
            },
            {
                name: 'endDate',
                validation: ValidationType.Date,
            },
        ];
    }

    @autobind
    private formatIncludeServiceParams(params: IncludeServiceParams[]): IncludeServiceParams[] {
        return params.map(param => ({
            id: param.id,
            share: this.formatIncludeServiceNumericField(param.share),
            discount: this.formatIncludeServiceNumericField(param.discount),
            usageLimit: this.formatIncludeServiceNumericField(param.usageLimit),
            agreement: param.agreement,
        }));
    }

    private formatIncludeServiceNumericField(value?: Nullable<number>): Optional<number> {
        return !isNil(value) ? value : undefined;
    }

    private formatMoney(value?: number): Optional<number> {
        return !isNil(value) ? Money.fromRubles(value).kopecks : undefined;
    }

    @autobind
    private formatAge(value: string | number): Nullable<number> {
        const stringValue = String(value);

        if (!Validation.isNumberString(stringValue)) {
            return null;
        }

        const age = Number(stringValue);

        return age < MAX_AGE ? age : MAX_AGE;
    }

    @autobind
    private onIncludeServicesSet(value: IncludeServiceParams[]): void {
        const includeServicesIds = new Set();
        value.forEach(item => includeServicesIds.add(item.id));

        this.excludeServices = this.excludeServices.filter(id => !includeServicesIds.has(id));
    }

    @autobind
    private onExcludeServicesSet(value: string[]): void {
        const excludeServicesIds = new Set(value);

        this.includeServices = this.includeServices.filter(item => !excludeServicesIds.has(item.id));
    }

    @autobind
    private onIncludeGroupsSet(value: IncludeServiceParams[]): void {
        const includeGroupsIds = new Set();
        value.forEach(item => includeGroupsIds.add(item.id));

        this.excludeGroups = this.excludeGroups.filter(id => !includeGroupsIds.has(id));
    }

    @autobind
    private onExcludeGroupsSet(value: string[]): void {
        const excludeGroupsIds = new Set(value);

        this.includeGroups = this.includeGroups.filter(item => !excludeGroupsIds.has(item.id));
    }
}
